import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { KickbackReportSource } from "@KassaroAB/shared/types/enums/KickbackSource";
import type { RestSetKickbackCompanyMatch } from "@KassaroAB/shared/types/invoices/kickback";
import {
  calculateVivaKickbackApi,
  getKickbackPeriodApi,
  setKickbackCompanyMatchApi,
  uploadKickbackReportApi,
} from "../api/kickbacksApi";

const kickbackPeriodKey = (period: string) => ["admin", "kickbacks", period];

const useInvalidateKickbackPeriod = (period: string) => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: kickbackPeriodKey(period) });
};

export const useGetKickbackPeriodQuery = (period: string) => {
  return useQuery({
    queryKey: kickbackPeriodKey(period),
    queryFn: () => getKickbackPeriodApi(period),
  });
};

export const useCalculateVivaKickbackMutation = (period: string) => {
  const invalidate = useInvalidateKickbackPeriod(period);
  return useMutation({
    mutationFn: (refresh: boolean) => calculateVivaKickbackApi(period, refresh),
    onSuccess: invalidate,
  });
};

export const useUploadKickbackReportMutation = (period: string) => {
  const invalidate = useInvalidateKickbackPeriod(period);
  return useMutation({
    mutationFn: ({ source, file }: { source: KickbackReportSource; file: File }) =>
      uploadKickbackReportApi(period, source, file),
    onSuccess: invalidate,
  });
};

export const useSetKickbackCompanyMatchMutation = (period: string) => {
  const invalidate = useInvalidateKickbackPeriod(period);
  return useMutation({
    mutationFn: (match: RestSetKickbackCompanyMatch) =>
      setKickbackCompanyMatchApi(period, match),
    onSuccess: invalidate,
  });
};
